import { useCallback, useMemo } from 'react';

import type { SessionUser } from '#src/shared/session';

import { useCurrentUser } from '#src/app/shared/hooks/session';

export type PpmuLocalizedHeadlineData = {
	totalSignatureCount: number;
	userCountryName: string;
	userStateName?: string;
};

type CountryOrStateSignatureCounts =
	| {
			totalSignatureCount: number;
	  }
	| null
	| undefined;

type UserLocation = {
	countryCode: string;
	countryDisplayName: string;
	stateCode: string;
	stateDisplayName: string | undefined;
};

function getUserCountry(currentUser: SessionUser | undefined) {
	return {
		countryCode: currentUser?.country?.countryCode ?? '',
		countryDisplayName: currentUser?.country?.displayName ?? '',
	};
}

function getUserState(currentUser: SessionUser | undefined) {
	return {
		stateCode: currentUser?.state?.stateCode ?? '',
		stateDisplayName: currentUser?.state?.displayName,
	};
}

function getUserLocation(currentUser: SessionUser | undefined): UserLocation {
	return {
		...getUserCountry(currentUser),
		...getUserState(currentUser),
	};
}

function validateData(userCountryCode: string, userStateCode: string, totalSignatureCount: number) {
	if (!userCountryCode || !userStateCode || totalSignatureCount < 1) {
		return false;
	}
	return true;
}

function getUserStateName(userCountryCode: string, userStateDisplayName: string | undefined) {
	if (userCountryCode === 'US' && userStateDisplayName) {
		return userStateDisplayName;
	}
	return undefined;
}

function buildHeadlineData(
	countryOrStateSignatureCounts: CountryOrStateSignatureCounts,
	userLocation: UserLocation,
): PpmuLocalizedHeadlineData | undefined {
	const totalSignatureCount = countryOrStateSignatureCounts?.totalSignatureCount ?? 0;
	if (!validateData(userLocation.countryCode, userLocation.stateCode, totalSignatureCount)) {
		return undefined;
	}
	return {
		totalSignatureCount,
		userCountryName: userLocation.countryDisplayName,
		userStateName: getUserStateName(userLocation.countryCode, userLocation.stateDisplayName),
	};
}

export function usePpmuLocalizedHeadlineData(): (
	countryOrStateSignatureCounts: CountryOrStateSignatureCounts,
) => PpmuLocalizedHeadlineData | undefined {
	const currentUser = useCurrentUser();
	const userLocation = useMemo(() => getUserLocation(currentUser), [currentUser]);

	return useCallback(
		(countryOrStateSignatureCounts: CountryOrStateSignatureCounts) =>
			buildHeadlineData(countryOrStateSignatureCounts, userLocation),
		[userLocation],
	);
}
